import React from 'react';
import SectionCard from './SectionCard';
import { useAppContext } from '../context/AppContext';
import { routeSpec } from '../ai/router/modelRouter';
import { deriveRunStatus, isStaleDraft } from '../utils/runStatus';

// D1: badge shown next to a row's query for any run whose derived status
// isn't 'complete'. A legacy/completed row (the vast majority, and every
// row that existed before D1) renders with no badge at all, i.e. identical
// to how HistoryTable always rendered it.
const STATUS_BADGES = {
  running:     { label: 'Running',     bg: '#38bdf822', color: '#38bdf8', border: '#38bdf855' },
  interrupted: { label: 'Interrupted', bg: '#f59e0b18', color: '#f59e0b', border: '#f59e0b55' },
  error:       { label: 'Error',       bg: '#f8717118', color: '#f87171', border: '#f8717155' },
  // A user-cancelled run (Cancel button) is neither a failure nor still in
  // flight; muted styling (the same #94a3b8 label/sub-header tone used
  // elsewhere) keeps it visually distinct from an actual error.
  cancelled:   { label: 'Cancelled',   bg: '#94a3b81c', color: '#94a3b8', border: '#94a3b855' },
  // D8: the plan-review window itself, before Generate was ever clicked --
  // deliberately the SAME muted tone as 'cancelled', never mistaken for an
  // in-flight or failed run: nothing has actually run yet.
  draft:       { label: 'Draft',       bg: '#94a3b81c', color: '#94a3b8', border: '#94a3b855' },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGES[status];
  if (!s) return null;
  return (
    <span
      title={status === 'interrupted' ? 'Run doc stayed ‘running’ with no further updates, the tab likely closed mid-run. Any candidates shown below were persisted before that happened.' : undefined}
      style={{
        display: 'inline-block', marginRight: 8, background: s.bg, color: s.color,
        border: `1px solid ${s.border}`, borderRadius: 6, padding: '1px 8px',
        fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', letterSpacing: 0.2,
      }}
    >
      {s.label}
    </span>
  );
}

// The unique services a list of rows names, in row order. Run docs are
// member-writable (firestore.rules checks membership only), so an entry that
// is not an object with a non-empty string `service` is skipped.
function rowServices(rows) {
  if (!Array.isArray(rows)) return [];
  const services = rows
    .map((r) => (r && typeof r === 'object' ? r.service : undefined))
    .filter((s) => typeof s === 'string' && s.length > 0);
  return [...new Set(services)];
}

/**
 * The History "Models" cell (editable-plan Part 1a). It names the models a
 * run actually executed, reading in order:
 * 0. an investigate run: 'Investigate';
 * 1. the services of h.executedRows, the rows the run dispatched (NL Execute,
 *    manual runs and Re-run write them);
 * 2. the services of h.reviewState.rows, a draft's reviewed rows;
 * 3. the spec's `models` (legacy manual runs);
 * 4. h.routerCalls (legacy run docs; new docs carry []);
 * 5. routeSpec over the spec, for a legacy run that stored none of these.
 * Returns '-' when nothing names a model, and on any throw.
 */
export function getModels(h) {
  try {
    if (h.mode === 'investigate') return 'Investigate';
    const executed = rowServices(h.executedRows);
    if (executed.length > 0) return executed.join(', ');
    const reviewed = rowServices(h.reviewState?.rows);
    if (reviewed.length > 0) return reviewed.join(', ');
    // Firestore runs store generationSpec; in-memory runs store plan.generation_spec
    const genSpec = h.generationSpec || h.plan?.generation_spec || {};
    if (Array.isArray(genSpec.models) && genSpec.models.length > 0) {
      return genSpec.models.join(', ');
    }
    // Router calls stored on a legacy run doc (Firestore)
    if (Array.isArray(h.routerCalls) && h.routerCalls.length > 0) {
      const services = [...new Set(h.routerCalls.map((c) => c.service))];
      return services.length > 0 ? services.join(', ') : '-';
    }
    const { calls } = routeSpec(genSpec);
    const services = [...new Set(calls.map((c) => c.service))];
    return services.length > 0 ? services.join(', ') : '-';
  } catch (_) {
    return '-';
  }
}

function HistoryTable({ history, onViewResults, onRerun, onInvestigateRerun, onResumeDraft }) {
  const ctx = useAppContext() || {};
  const allRows = Array.isArray(history) ? history : (Array.isArray(ctx.history) ? ctx.history : []);
  // D8: an abandoned draft (the plan was produced, review window opened,
  // never promoted by clicking Generate) is never deleted -- it just sits
  // there. Hiding one older than 7 days here, read-side, keeps History from
  // slowly filling up with plans nobody ever came back to, without needing
  // any backend prune job (there's no orchestrator for run docs at all, see
  // D1's own note on this).
  const rows = allRows.filter((h) => !isStaleDraft(h));
  const viewResults = onViewResults || (ctx.viewHistoryRun ? (h) => ctx.viewHistoryRun(h) : null);
  const rerun = onRerun || (ctx.rerunRun ? (h) => ctx.rerunRun(h) : null);
  const resumeDraft = onResumeDraft || (ctx.resumeDraft ? (h) => ctx.resumeDraft(h) : null);
  // M10: AppProvider's investigate entry point is onInvestigateSubmit —
  // ctx.investigateRerun never existed, making Re-run a silent no-op here.
  const investigateRerun = onInvestigateRerun || ctx.investigateRerun || ctx.onInvestigateSubmit || null;
  const busy = ctx.loading || false;
  if (!rows || rows.length === 0) return null;

  function handleRerun(h) {
    if (h.mode === 'investigate') {
      if (investigateRerun) {
        const formula = h.description || h.userQuery?.replace(/^Investigate:\s*/i, '') || '';
        investigateRerun({ formula });
      }
      // Do NOT call rerun(null) for investigate-mode runs (CRI-7)
    } else if (rerun) {
      // Part 1a (1.11): Re-run replays the rows this run executed, so it gets the whole row.
      rerun(h);
    }
  }

  return (
    <SectionCard title="History">
      <div className="table-responsive">
        <table className="table table-dark table-striped table-hover align-middle mb-0 text-light">
          <thead>
            <tr>
              <th>When</th>
              <th>Query</th>
              <th>Models</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => {
              const showRerunBtn = h.mode === 'investigate' ? (investigateRerun != null || rerun != null) : rerun != null;
              // D1: derived read-side, a run doc left 'running' with a stale
              // updatedAt (no backend orchestrator ever comes back to mark it
              // otherwise) renders as 'interrupted' here, with whatever
              // partial candidateCount/results were persisted before the tab
              // died. A row with no status field at all (every run written
              // before D1) derives to 'complete' and renders with no badge,
              // identical to how this table always rendered it.
              const runStatus = deriveRunStatus(h);
              const isInFlight = runStatus === 'running' || runStatus === 'interrupted';
              // D8: a draft has no results and nothing to re-run -- "View
              // Results" would open an empty results page and "Re-run" would
              // dispatch compute for a plan the user never actually reviewed/
              // approved via Generate. Resume (opens the plan-review window
              // back up from the persisted reviewState, or rebuilds a legacy
              // draft's rows once) is the only honest action for this row.
              const isDraft = runStatus === 'draft';
              return (
                <tr key={h.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{new Date(h.ts).toLocaleString()}</td>
                  <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#cbd5e1', fontSize: 13 }}>
                    <StatusBadge status={runStatus} />
                    {h.description || h.userQuery || h.plan?.generation_spec?.meta?.user_prompt_raw || '—'}
                    {isInFlight && Number(h.candidateCount) > 0 && (
                      <span style={{ color: '#64748b', marginLeft: 6 }}>({h.candidateCount} so far)</span>
                    )}
                  </td>
                  <td>{getModels(h)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {isDraft ? (
                        resumeDraft && (<button className="btn btn-outline-light btn-sm" disabled={busy} onClick={() => resumeDraft(h)}>{busy ? 'Loading…' : 'Resume'}</button>)
                      ) : (
                        <>
                          {viewResults && (<button className="btn btn-outline-light btn-sm" disabled={busy} onClick={() => viewResults(h)}>{busy ? 'Loading…' : 'View Results'}</button>)}
                          {showRerunBtn && (<button className="btn btn-primary btn-sm" disabled={busy} onClick={() => handleRerun(h)}>{busy ? 'Running…' : 'Re-run'}</button>)}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

export default HistoryTable;
